/**
 * Página de Histórico Unificado via Timeline
 */

import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, History, RefreshCw } from "lucide-react";
import { useHistorico } from "../../hooks/useHistorico";
import type { TimelineSourceFilter } from "../../types/historico";
import { HistoricoSourceChips } from "./components/HistoricoSourceChips";
import { HistoricoFilters } from "./components/HistoricoFilters";
import { HistoricoList } from "./components/HistoricoList";
import { HistoricoEmptyState } from "./components/HistoricoEmptyState";
import { HistoricoErrorState } from "./components/HistoricoErrorState";
import { Button } from "../ui";

export function HistoricoPage() {
	const navigate = useNavigate();
	const {
		items,
		loading,
		error,
		filters,
		sourcesCount,
		setFilters,
		loadMore,
		refresh,
		hasMore,
	} = useHistorico("all");

	const handleSourceChange = useCallback(
		(source: TimelineSourceFilter) => {
			setFilters((prev) => ({ ...prev, source, page: 1 }));
		},
		[setFilters]
	);

	const handleSearchChange = useCallback(
		(searchTerm: string) => {
			setFilters((prev) => ({ ...prev, searchTerm }));
		},
		[setFilters]
	);

	const hasError = !!error;
	const hasItems = items.length > 0;
	const showEmpty = !loading && !hasError && !hasItems;

	return (
		<div className="h-full flex flex-col bg-navy-900">
			{/* Header */}
			<div className="flex items-center gap-2 p-3 border-b border-navy-700">
				<Button
					variant="ghost"
					size="sm"
					onClick={() => navigate("/main")}
					icon={ArrowLeft}
					title="Voltar"
					aria-label="Voltar"
					className="p-1.5 min-h-[44px] min-w-[44px]"
				/>
				<History className="w-5 h-5 text-blue-400 flex-shrink-0" />
				<h1 className="text-lg font-semibold text-white truncate flex-1">
					Histórico
				</h1>
				<Button
					variant="ghost"
					size="sm"
					onClick={refresh}
					disabled={loading}
					icon={RefreshCw}
					title="Atualizar"
					aria-label="Atualizar"
					className={`p-1.5 min-h-[44px] min-w-[44px] ${loading ? "animate-spin" : ""}`}
				/>
			</div>

			{/* Source Chips */}
			<div className="p-3 border-b border-navy-700">
				<HistoricoSourceChips
					activeSource={filters.source || "all"}
					onSourceChange={handleSourceChange}
					sourcesCount={sourcesCount}
					loading={loading}
				/>
			</div>

			{/* Content */}
			<div className="flex-1 overflow-hidden flex flex-col">
				{/* Filtros */}
				<div className="p-3 border-b border-navy-700">
					<HistoricoFilters
						searchTerm={filters.searchTerm || ""}
						onSearchChange={handleSearchChange}
						loading={loading}
					/>
				</div>

				{/* Erro */}
				{hasError && (
					<div className="p-3">
						<HistoricoErrorState error={error} onRetry={refresh} />
					</div>
				)}

				{/* Lista */}
				<div className="flex-1 overflow-auto">
					{loading && !hasItems ? (
						<HistoricoListSkeleton />
					) : showEmpty ? (
						<HistoricoEmptyState
							searchTerm={filters.searchTerm}
							sourceFilter={filters.source}
						/>
					) : (
						<HistoricoList
							items={items}
							loading={loading}
							hasMore={hasMore}
							onLoadMore={loadMore}
						/>
					)}
				</div>
			</div>
		</div>
	);
}

// Skeleton de loading
function HistoricoListSkeleton() {
	return (
		<div className="p-4 space-y-3">
			{[1, 2, 3, 4, 5].map((i) => (
				<div key={i} className="animate-pulse">
					<div className="bg-navy-800 rounded-lg p-4 space-y-2">
						<div className="h-4 w-3/4 bg-navy-700 rounded"></div>
						<div className="h-3 w-1/2 bg-navy-700 rounded"></div>
						<div className="h-3 w-full bg-navy-700 rounded"></div>
					</div>
				</div>
			))}
		</div>
	);
}
